import { DashboardModal } from "@dashboard/components/Modal";
import { Box, Button, Input, Select, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";

import { PriceTier } from "../types";

export interface TierFormData {
  name: string;
  minQuantity: string;
  discountType: "percentage" | "fixed";
  discountValue: string;
  isActive: boolean;
}

interface TieredPricingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: TierFormData) => void;
  loading?: boolean;
  editingTier?: PriceTier | null;
}

const EMPTY_FORM: TierFormData = {
  name: "",
  minQuantity: "1",
  discountType: "percentage",
  discountValue: "",
  isActive: true,
};

const DISCOUNT_TYPE_OPTIONS = [
  { label: "Percentage (%)", value: "percentage" },
  { label: "Fixed Amount ($)", value: "fixed" },
];

export const TieredPricingModal: React.FC<TieredPricingModalProps> = ({
  open,
  onClose,
  onSave,
  loading = false,
  editingTier = null,
}) => {
  const [formData, setFormData] = useState<TierFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof TierFormData, string>>>({});

  useEffect(() => {
    if (open) {
      if (editingTier) {
        setFormData({
          name: editingTier.name,
          minQuantity: String(editingTier.minQuantity),
          discountType: editingTier.discountType,
          discountValue: String(editingTier.discountValue),
          isActive: editingTier.isActive,
        });
      } else {
        setFormData(EMPTY_FORM);
      }

      setErrors({});
    }
  }, [open, editingTier]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TierFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Tier name is required";
    }

    const qty = parseInt(formData.minQuantity, 10);

    if (isNaN(qty) || qty < 1) {
      newErrors.minQuantity = "Minimum quantity must be at least 1";
    }

    const val = parseFloat(formData.discountValue);

    if (isNaN(val) || val <= 0) {
      newErrors.discountValue = "Discount value must be greater than 0";
    }

    if (formData.discountType === "percentage" && val > 100) {
      newErrors.discountValue = "Percentage discount cannot exceed 100%";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(formData);
    }
  };

  const update = (field: keyof TierFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  return (
    <DashboardModal open={open} onChange={onClose}>
      <DashboardModal.Content size="lg">
        <DashboardModal.Title>
          {editingTier ? "Edit Price Tier" : "Add Price Tier"}
        </DashboardModal.Title>

        <Box display="flex" flexDirection="column" gap={4} __minWidth="400px">
          {/* Tier Name */}
          <Box display="flex" flexDirection="column" gap={1}>
            <Input
              label="Tier Name"
              value={formData.name}
              onChange={e => update("name", e.target.value)}
              disabled={loading}
              error={!!errors.name}
              helperText={errors.name}
              placeholder="e.g. Wholesale, VIP, Bulk"
            />
          </Box>

          {/* Min Quantity */}
          <Box display="flex" flexDirection="column" gap={1}>
            <Input
              label="Minimum Quantity"
              type="number"
              value={formData.minQuantity}
              onChange={e => update("minQuantity", e.target.value)}
              disabled={loading}
              error={!!errors.minQuantity}
              helperText={errors.minQuantity ?? "Order must have at least this many items to qualify"}
            />
          </Box>

          {/* Discount Type */}
          <Box display="flex" flexDirection="column" gap={1}>
            <Select
              label="Discount Type"
              value={formData.discountType}
              onChange={value => update("discountType", value as string)}
              disabled={loading}
              options={DISCOUNT_TYPE_OPTIONS}
            />
          </Box>

          {/* Discount Value */}
          <Box display="flex" flexDirection="column" gap={1}>
            <Input
              label={formData.discountType === "percentage" ? "Discount (%)" : "Discount Amount ($)"}
              type="number"
              value={formData.discountValue}
              onChange={e => update("discountValue", e.target.value)}
              disabled={loading}
              error={!!errors.discountValue}
              helperText={errors.discountValue}
              placeholder={formData.discountType === "percentage" ? "e.g. 10" : "e.g. 5.00"}
            />
          </Box>

          {/* Active Toggle */}
          <Box display="flex" alignItems="center" gap={3}>
            <input
              type="checkbox"
              id="tier-active"
              checked={formData.isActive}
              onChange={e => update("isActive", e.target.checked)}
              disabled={loading}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
            <Text
              as="span"
              htmlFor="tier-active"
              style={{ cursor: "pointer" }}
            >
              Active
            </Text>
          </Box>
        </Box>

        <DashboardModal.Actions>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {editingTier ? "Save Changes" : "Add Tier"}
          </Button>
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};
